import { Box } from '@mui/material';
import get from 'lodash/get';
import { ListDataItem } from '/src/lib/types';

export type ListHeaderProps = {
  /**
   * List data items
   */
  items: ListDataItem[];
  /**
   * Field to sort by
   */
  sortType: string;
  /**
   * Sorts the list in reverse order
   */
  sortReverse: boolean;
  /**
   * Sorts the data based on the given prop name
   * @param propName Prop to sort by
   * @returns void
   */
  sortList: (propName: string) => void;
  /**
   * Header items text alignment
   */
  textAlign?: 'left' | 'center' | 'right';
  /**
   * Data row height. Defaults to 48px
   */
  rowHeight?: number;
  /**
   * Shows/hides data items
   */
  viewOptions?: Record<string, boolean | undefined>;
  /**
   * Whether to adjust padding to account for a scrollbar. Used for longer lists
   */
  padForScrollbar?: boolean;
  /**
   * Don't show up/down arrows next to the current `sortType` header item
   */
  hideSortIcon?: boolean;
};

export const ListHeader = ({
  items,
  sortType,
  sortReverse,
  sortList,
  rowHeight = 48,
  viewOptions,
  padForScrollbar,
  hideSortIcon,
}: ListHeaderProps) => {
  return (
    <Box
      paddingX="16px"
      display="flex"
      alignItems="center"
      className={`medium md-caption sortable-list ${padForScrollbar ? 'padForScrollbar' : ''}`}
      sx={{ height: rowHeight }}
    >
      {items
        .filter((item) => !item.hide && (viewOptions ? typeof get(viewOptions, item.propName) === 'boolean' : true))
        .map(({ flex = 1, propName, label, textAlign }, i) => (
          <Box
            key={`${propName}-${i}`}
            sx={{ cursor: 'pointer' }}
            display="flex"
            flex={flex}
            justifyContent={textAlign === 'left' ? 'start' : textAlign === 'right' ? 'end' : textAlign}
            alignItems="center"
            gap="3px"
            maxWidth={flex && flex > 1 ? flex + '%' : undefined}
            width="100%"
            onClick={() => sortList(propName as string)}
          >
            {label}
            {!hideSortIcon && sortType === propName && <Box className={sortReverse ? 'arrow-down' : 'arrow-up'} />}
          </Box>
        ))}
    </Box>
  );
};
